/**
 * ForgotPassword page — request a password reset link.
 * Re-skinned with consentry-v3 design system.
 */

import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateForm = (): boolean => {
    setEmailError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Email address is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await apiFetch('/v1/auth/forgot-password', {
        method: 'POST',
        body: { email: email.trim() },
      });
      setSubmitted(true);
      toast.success('Password reset link sent to your email.');
    } catch (err) {
      const msg = getNetworkErrorMessage(err, 'Failed to send password reset link.');
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Forgot Password?"
      subtitle="Enter your email address and we'll send you a password reset link"
    >
      {submitted ? (
        <div className="space-y-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald/10 border border-emerald/20 flex items-center justify-center text-emerald text-xl font-bold">
            ✓
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-bold text-ink">Check your inbox</h3>
            <p className="text-xs text-sub leading-relaxed">
              If an account exists for <span className="font-semibold text-ink">{email}</span>, a
              password reset link has been dispatched to that address.
            </p>
          </div>

          <div className="pt-4 space-y-3">
            <Button
              type="button"
              variant="btn-g"
              fullWidth
              onClick={() => {
                setSubmitted(false);
                setEmail('');
              }}
            >
              Send link to another email
            </Button>

            <Link
              to="/login"
              className="inline-block text-xs font-semibold text-blue hover:underline pt-2"
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Input
            label="Email Address"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
              if (apiError) setApiError(null);
            }}
            error={emailError || undefined}
            disabled={isSubmitting}
            required
            autoFocus
            leftIcon={
              <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            }
          />

          {apiError && (
            <div className="rounded-xl border border-err/20 bg-err/5 p-3.5 text-xs text-err font-medium flex items-center gap-2.5">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{apiError}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="btn-p"
            fullWidth
            isLoading={isSubmitting}
            loadingText="Sending reset link..."
            disabled={isSubmitting || !email.trim()}
            className="mt-2"
          >
            Send Reset Link
          </Button>

          <p className="text-center text-xs text-sub font-medium mt-4">
            Remember your password?{' '}
            <Link to="/login" className="text-blue hover:underline font-semibold">
              Sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

export const ForgotPassword = ForgotPasswordPage;
